import { useMemo } from 'react';

import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';

import { UnitSystem } from '../../../types/util.ts';
import { getLengthUnitLabel, toDisplayLength } from '../../../utils/unitSystem.ts';

interface MeasurementLineProps {
  points: [number, number, number][];
  distance: number | null;
  segmentDistances?: number[];
  hoverPoint?: [number, number, number] | null;
  isHoverSnapped?: boolean;
  showCtrlHint?: boolean; // Show hint when CTRL/CMD is required for first click
  labelVariant?: 'default' | 'compact';
  unitSystem: UnitSystem;
}

// ===== CONSTANTS =====
const GREEN_COLOR = '#00FF00';
const GREY_OUTLINE_COLOR = '#1A1A1A';

// Render order constants for visual stability
// Higher numbers render on top
const RENDER_ORDER = {
  LINE_OUTLINE: 997, // Grey outline - behind green line
  LINE_CORE: 998, // Green line - on top of outline
  SPHERE_OUTLINE: 999, // Sphere outline
  SPHERE_CORE: 1000, // Sphere core - topmost
} as const;

/**
 * Endpoint Sphere Component
 * Bright green sphere with dark outline
 * Uses raycast={null} to prevent self-intersection during measurement
 */
function EndpointSphere({ position, isHover = false }: { position: [number, number, number]; isHover?: boolean }) {
  const outerRadius = isHover ? 0.18 : 0.15;
  const innerRadius = isHover ? 0.12 : 0.1;

  return (
    <group position={position}>
      {/* Dark grey outline sphere (larger, behind) */}
      <mesh
        renderOrder={RENDER_ORDER.SPHERE_OUTLINE}
        raycast={() => null} // Prevent self-intersection
        userData={{ isMeasurement: true }}
      >
        <sphereGeometry args={[outerRadius, 32, 32]} />
        <meshBasicMaterial color={GREY_OUTLINE_COLOR} depthTest={false} depthWrite={false} transparent opacity={1} />
      </mesh>
      {/* Bright green inner sphere (smaller, on top) */}
      <mesh
        renderOrder={RENDER_ORDER.SPHERE_CORE}
        raycast={() => null} // Prevent self-intersection
        userData={{ isMeasurement: true }}
      >
        <sphereGeometry args={[innerRadius, 32, 32]} />
        <meshBasicMaterial color={GREEN_COLOR} depthTest={false} depthWrite={false} transparent opacity={1} />
      </mesh>
    </group>
  );
}

/**
 * Outlined Line Component
 * Uses multi-pass technique: thick dark grey line behind thin bright green line
 * Consistent renderOrder ensures grey is always behind green
 * Both render on top of scene (depthTest: false)
 */
function OutlinedLine({
  start,
  end,
  isRubberBand = false,
}: {
  start: [number, number, number];
  end: [number, number, number];
  isRubberBand?: boolean;
}) {
  const points: [number, number, number][] = [start, end];

  const outerWidth = isRubberBand ? 4 : 5;
  const innerWidth = isRubberBand ? 1.5 : 2;
  const opacity = isRubberBand ? 0.85 : 1;

  return (
    <group userData={{ isMeasurement: true }}>
      {/* Dark grey outline (thicker, rendered BEHIND green line) */}
      <Line
        points={points}
        color={GREY_OUTLINE_COLOR}
        lineWidth={outerWidth}
        depthTest={false}
        depthWrite={false}
        transparent
        opacity={opacity}
        renderOrder={RENDER_ORDER.LINE_OUTLINE}
        raycast={() => null} // Prevent self-intersection
      />
      {/* Bright green line (thinner, rendered ON TOP of grey) */}
      <Line
        points={points}
        color={GREEN_COLOR}
        lineWidth={innerWidth}
        depthTest={false}
        depthWrite={false}
        transparent
        opacity={opacity}
        renderOrder={RENDER_ORDER.LINE_CORE}
        raycast={() => null} // Prevent self-intersection
      />
    </group>
  );
}

/**
 * Distance Label Component
 * Shows distance value with high-contrast styling
 * Uses pointerEvents: 'none' to not block clicks
 */
function DistanceLabel({
  position,
  distance,
  isRubberBand = false,
  variant = 'default',
  unitSystem,
}: {
  position: THREE.Vector3;
  distance: number;
  isRubberBand?: boolean;
  variant?: 'default' | 'compact';
  unitSystem: UnitSystem;
}) {
  const isCompact = variant === 'compact';
  const displayDistance = toDisplayLength(distance, unitSystem);
  const lengthUnitLabel = getLengthUnitLabel(unitSystem);

  return (
    <Html
      position={position}
      center
      style={{ pointerEvents: 'none' }} // Critical: don't block clicks
      zIndexRange={[100, 0]} // Ensure proper z-ordering
    >
      <div
        style={{
          background: isCompact ? 'rgba(0, 0, 0, 0.7)' : GREEN_COLOR,
          borderRadius: isCompact ? '4px' : isRubberBand ? '4px' : '6px',
          padding: isCompact ? '4px 8px' : isRubberBand ? '4px 10px' : '6px 14px',
          fontSize: isCompact ? '12px' : isRubberBand ? '13px' : '16px',
          fontWeight: isCompact ? 600 : 700,
          color: isCompact ? '#B3EC49' : '#000',
          whiteSpace: 'nowrap',
          boxShadow: isCompact ? undefined : '0 4px 12px rgba(0,0,0,0.5)',
          border: isCompact ? undefined : `2px solid ${GREY_OUTLINE_COLOR}`,
          opacity: isRubberBand ? 0.9 : 1,
          userSelect: 'none',
          pointerEvents: 'none', // Redundant but explicit
        }}
      >
        {displayDistance.toFixed(2)} {lengthUnitLabel}
      </div>
    </Html>
  );
}

/**
 * Main MeasurementLine Component
 *
 * Renders measurement visualization with:
 * - Bright green spheres at start/end points
 * - Green line with dark grey border (multi-pass technique)
 * - Distance label at midpoint
 * - All elements render on top (depthTest: false)
 * - All elements ignore raycasting (raycast: null) to prevent self-intersection
 */
/** Compute the label midpoint between two 3D points with a vertical offset. */
function computeSegmentMidpoint(p1: [number, number, number], p2: [number, number, number]): THREE.Vector3 {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const lineLength = Math.sqrt(dx * dx + dy * dy);
  const zOffset = lineLength < 2 ? 1.2 : lineLength < 5 ? 0.8 : 0.5;
  return new THREE.Vector3((p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2, Math.max(p1[2], p2[2]) + zOffset);
}

export function MeasurementLine({
  points,
  distance,
  segmentDistances,
  hoverPoint,
  isHoverSnapped: _isHoverSnapped = false,
  showCtrlHint = false,
  labelVariant = 'default',
  unitSystem,
}: MeasurementLineProps) {
  const isMultiSegment = points.length > 2;

  // Calculate rubber-band data (line preview while drawing)
  const rubberBandData = useMemo(() => {
    if (points.length === 0 || !hoverPoint) return null;

    const p1 = points[points.length - 1]; // last placed point
    const p2 = hoverPoint;

    const start = new THREE.Vector3(p1[0], p1[1], p1[2]);
    const end = new THREE.Vector3(p2[0], p2[1], p2[2]);
    const dist = start.distanceTo(end);

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const lineLength = Math.sqrt(dx * dx + dy * dy);
    const zOffset = lineLength < 2 ? 1.0 : lineLength < 5 ? 0.7 : 0.4;

    const mid = new THREE.Vector3((p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2, Math.max(p1[2], p2[2]) + zOffset);

    return { distance: dist, midpoint: mid };
  }, [points, hoverPoint]);

  // Nothing to render (unless we need to show the CTRL hint)
  if (points.length === 0 && !hoverPoint && !showCtrlHint) return null;

  return (
    <group userData={{ isMeasurement: true }}>
      {/* Endpoint spheres for all placed points */}
      {points.map((point, index) => (
        <EndpointSphere key={`endpoint-${index}`} position={point} />
      ))}

      {/* Lines between consecutive points */}
      {points.length >= 2 &&
        points.map((point, index) => {
          if (index === 0) return null;
          const start = points[index - 1];
          const end = point;
          const segDist = segmentDistances?.[index - 1];
          const mid = computeSegmentMidpoint(start, end);

          return (
            <group key={`segment-${index}`}>
              <OutlinedLine start={start} end={end} />
              {segDist != null && (
                <DistanceLabel
                  position={mid}
                  distance={segDist}
                  variant={isMultiSegment ? 'compact' : labelVariant}
                  unitSystem={unitSystem}
                />
              )}
            </group>
          );
        })}

      {/* Total distance label for multi-segment (near the last point) */}
      {isMultiSegment && distance !== null && (
        <Html
          position={[points[points.length - 1][0], points[points.length - 1][1], points[points.length - 1][2] + 1.8]}
          center
          style={{ pointerEvents: 'none' }}
          zIndexRange={[100, 0]}
        >
          <div
            style={{
              background: GREEN_COLOR,
              borderRadius: '6px',
              padding: '6px 14px',
              fontSize: '16px',
              fontWeight: 700,
              color: '#000',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              border: `2px solid ${GREY_OUTLINE_COLOR}`,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            Σ {toDisplayLength(distance, unitSystem).toFixed(2)} {getLengthUnitLabel(unitSystem)}
          </div>
        </Html>
      )}

      {/* Rubber-band preview (from last point to hover position) */}
      {points.length >= 1 && hoverPoint && (
        <>
          <EndpointSphere position={hoverPoint} isHover />
          <OutlinedLine start={points[points.length - 1]} end={hoverPoint} isRubberBand />
          {rubberBandData && (
            <DistanceLabel
              position={rubberBandData.midpoint}
              distance={rubberBandData.distance}
              isRubberBand
              variant={labelVariant}
              unitSystem={unitSystem}
            />
          )}
        </>
      )}
    </group>
  );
}
